import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ADD_CANDIDATE } from '../../../GraphQL/candidates/mutations';
import { GET_CANDIDATES } from '../../../GraphQL/candidates/queries';
import { GET_LOCALITIES } from '../../../GraphQL/settings/queries';
import { ProfileContext } from '../../../App';
import { autoDissmissOnAction } from '../utils/ctaDismissal';
import { SuccessFeedback } from '../components/SuccessFeedback';

interface AddCandidateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  jobTitle: string;
  idealJobTitles: string;
  location: string;
}

// Enhanced Dialog with animations
const StyledDialogContent = styled(DialogContent)`
  animation: scaleIn 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  border-radius: 8px;
  max-width: 600px;

  input, textarea {
    transition: border-color 200ms ease-in-out, box-shadow 200ms ease-in-out;

    &:focus {
      border-color: #227B7E;
      box-shadow: 0 0 0 0.25rem rgba(34, 123, 126, 0.15);
    }
  }

  button {
    transition: transform 150ms ease-in-out;

    &:active:not(:disabled) {
      transform: scale(0.97);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    button:active:not(:disabled) {
      transform: none;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormText = styled.p`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
`;

const InvalidFeedback = styled.div`
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.25rem;
`;

const RequiredStar = styled.span`
  color: #dc3545;
`;

/**
 * AddCandidateModal - Quick add candidate modal with minimal fields
 * Provides sensible defaults for all required backend fields
 */
export function AddCandidateModal({ isOpen, onClose, onSuccess }: AddCandidateModalProps) {
  const { userProfile } = useContext(ProfileContext);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    jobTitle: '',
    idealJobTitles: '',
    location: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch localities for location lookup
  const { data: localitiesData } = useQuery(GET_LOCALITIES, {
    variables: { search: formData.location || 'Sydney' },
    skip: !formData.location,
  });

  const [addCandidate, { loading }] = useMutation(ADD_CANDIDATE, {
    refetchQueries: [
      { query: GET_CANDIDATES, variables: { skip: 0, take: 50, order: { updatedAt: 'DESC' } } },
    ],
  });

  /**
   * Get default locality ID (Sydney, Australia as fallback)
   */
  const getLocalityId = (): number => {
    if (localitiesData?.localities?.items?.length > 0) {
      return localitiesData.localities.items[0].id;
    }
    // Fallback to Sydney, Australia
    return 20374;
  };

  /**
   * Get sensible defaults for all required fields
   */
  const getDefaults = () => {
    return {
      firstName: formData.firstName,
      lastName: formData.lastName,
      middleName: '',
      summary: 'Candidate details to be confirmed',
      salaryMin: 0,
      salaryMax: 0,
      yearsOfExperience: 0,
      idealJobTitles: formData.idealJobTitles || formData.jobTitle || 'Open to opportunities',
      workType: 'PERMANENT',
      industry: userProfile?.industryPreferences?.[0] || 'Technology',
      jobTitle: formData.jobTitle || 'Professional',
      interviewed: false,
      share: false,
      audience: 'PRIVATE',
      localityId: getLocalityId(),
      currency: 'AUD',
      ownerUserIds: [userProfile?.id].filter(Boolean),
      projectCandidateBoardIds: [],
    };
  };

  /**
   * Handles form submission
   */
  const handleSubmit = async (closeAfter: boolean) => {
    setError(null);

    // Validation
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }

    try {
      const variables = getDefaults();

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await addCandidate({ variables });

      // Show success animation
      setShowSuccess(true);

      // Auto-dismiss related CTAs
      autoDissmissOnAction('first_candidate');

      onSuccess();

      // Wait for success animation, then close or reset
      setTimeout(() => {
        setShowSuccess(false);
        if (closeAfter) {
          onClose();
          // Reset form
          setFormData({
            firstName: '',
            lastName: '',
            jobTitle: '',
            idealJobTitles: '',
            location: '',
          });
        } else {
          // Reset form for "Add Another"
          setFormData({
            firstName: '',
            lastName: '',
            jobTitle: '',
            idealJobTitles: '',
            location: '',
          });
        }
      }, 1500);
    } catch (err: any) {
      console.error('Error adding candidate:', err);
      setError(err.message || 'Failed to add candidate. Please try again.');
    }
  };

  /**
   * Handles modal close
   */
  const handleClose = () => {
    if (!loading && !showSuccess) {
      setFormData({
        firstName: '',
        lastName: '',
        jobTitle: '',
        idealJobTitles: '',
        location: '',
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <StyledDialogContent>
        <DialogHeader>
          <DialogTitle>Add New Candidate</DialogTitle>
        </DialogHeader>

        {showSuccess ? (
          <SuccessFeedback
            message="Candidate added successfully!"
            subtext="You can now match this candidate to roles"
          />
        ) : (
          <>
            <div>
              <FormGroup>
                <Label htmlFor="firstName">
                  First Name <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="e.g., John"
                  autoFocus
                  className={error === 'First name is required' ? 'border-red-500' : ''}
                />
                {error === 'First name is required' && (
                  <InvalidFeedback>First name is required</InvalidFeedback>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">
                  Last Name <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="e.g., Smith"
                  className={error === 'Last name is required' ? 'border-red-500' : ''}
                />
                {error === 'Last name is required' && (
                  <InvalidFeedback>Last name is required</InvalidFeedback>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="jobTitle">Current Job Title (Optional)</Label>
                <Input
                  id="jobTitle"
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                  placeholder="e.g., Senior Software Engineer"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="idealJobTitles">Ideal Job Titles (Optional)</Label>
                <Input
                  id="idealJobTitles"
                  type="text"
                  value={formData.idealJobTitles}
                  onChange={(e) => setFormData({ ...formData, idealJobTitles: e.target.value })}
                  placeholder="e.g., Tech Lead, Engineering Manager"
                />
                <FormText>
                  Comma-separated list of roles they're interested in
                </FormText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="location">Location (Optional)</Label>
                <Input
                  id="location"
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Sydney, Melbourne, Remote"
                />
              </FormGroup>

              {error && error !== 'First name is required' && error !== 'Last name is required' && (
                <Alert variant="destructive" className="mb-3">
                  <AlertDescription>
                    <strong>Unable to add candidate</strong>
                    <p className="mt-1">{error}</p>
                    <small className="text-xs">
                      You can still edit all details later by going to the candidate settings.
                    </small>
                  </AlertDescription>
                </Alert>
              )}

              <Alert className="mb-3" style={{ fontSize: '0.875rem' }}>
                <AlertDescription>
                  <strong>💡 Quick Add:</strong> This creates a private candidate record with default
                  values. You can add full profile details (resume, experience, salary expectations,
                  etc.) in the candidate settings later.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSubmit(false)}
                disabled={!formData.firstName.trim() || !formData.lastName.trim() || loading}
              >
                {loading ? 'Adding...' : 'Add & Create Another'}
              </Button>
              <Button
                onClick={() => handleSubmit(true)}
                disabled={!formData.firstName.trim() || !formData.lastName.trim() || loading}
              >
                {loading ? 'Adding...' : 'Add Candidate'}
              </Button>
            </DialogFooter>
          </>
        )}
      </StyledDialogContent>
    </Dialog>
  );
}
